import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import styles from "./W1Page.module.css";
import W1Sidebar from "./W1Sidebar";
import W1Table from "./W1Table";
import { wf1Api, genIdempotencyKey } from "../api";

/* Helpers */
const ativoExigePatios = (nomeAtivo) => /^[WVE]\d/i.test(String(nomeAtivo || "").trim());

/**
 * Quantidade (inteiro) - aceita:
 * - "1000"
 * - "1.000" / "12.345.678" (milhar pt-BR)
 * - "1.0" ou "1.00" (decimal com zeros) => vira 1
 * Rejeita "1.5" etc.
 */
function parseQtdIntBR(raw) {
  const s = String(raw ?? "").trim();
  if (!s) return null;

  // só dígitos
  if (/^\d+$/.test(s)) return Number(s);

  // milhar pt-BR (1.000 / 12.345.678)
  if (/^\d{1,3}(\.\d{3})+$/.test(s)) return Number(s.replace(/\./g, ""));

  // decimal com ponto, somente se fração for zeros (1.0 / 1.00 / 217.000 -> 217)
  if (/^\d+\.\d+$/.test(s)) {
    const [a, b] = s.split(".");
    if (/^0+$/.test(b)) return Number(a);
    return null;
  }

  // tolerância: se vier com espaços ou outros símbolos, tenta extrair só dígitos
  // MAS: só aplica se não houver ponto "estranho" (evita 1.2 virar 12)
  if (!s.includes(".")) {
    const digits = s.replace(/[^\d]/g, "");
    return digits ? Number(digits) : null;
  }

  return null;
}

const linhaVazia = (id) => ({
  id,
  codigo_unico: "",
  request_id: "",
  data: "",
  atividade: "",
  unidade_de_medida: "",
  ativo: "",
  equipamento: "",
  tempo_do_intervalo: "",
  sub: "",
  sbOrigem: "",
  sbDeTrabalho: "",
  kmInicio: "",
  kmFinal: "",
  sbEncerramento: "",
  os: "",
  quantidade: "",
  observacao: "",
  nome_do_responsavel: "",
  telefone_do_reponsavel: "",
  kmValido: true,
  kmMsg: "",
});

export default function W1Page({ onBack }) {
  const [gerencias, setGerencias] = useState([]);
  const [gerencia, setGerencia] = useState("");
  const [coordenadores, setCoordenadores] = useState([]);
  const [coordenador, setCoordenador] = useState("");

  const [subs, setSubs] = useState([]);
  const [atividades, setAtividades] = useState([]);
  const [ativos, setAtivos] = useState([]);
  const [sboePairs, setSboePairs] = useState([]);

  const [linhas, setLinhas] = useState([linhaVazia(1)]);
  const [nextId, setNextId] = useState(2);
  const [salvando, setSalvando] = useState(false);
  const [erroSalvar, setErroSalvar] = useState("");

  /* loads */
  useEffect(() => {
    wf1Api.getGerencias().then((r) => setGerencias(r.data.data || []));
    wf1Api.getAtividades().then((r) => setAtividades(r.data.data || []));
    wf1Api.getAtivos().then((r) => setAtivos(r.data.data || []));
    wf1Api.getSBsDistinct().then((r) => setSboePairs(Array.isArray(r.data.data) ? r.data.data : []));
  }, []);

  useEffect(() => {
    if (gerencia) {
      wf1Api.getCoordenadores(gerencia).then((r) => setCoordenadores(r.data.data || []));
    } else {
      setCoordenadores([]);
      setCoordenador("");
    }
  }, [gerencia]);

  useEffect(() => {
    if (coordenador) {
      wf1Api.getSubs(coordenador).then((r) => setSubs(r.data.data || []));
    } else {
      setSubs([]);
    }
    // reset campos dependentes
    setLinhas((ls) => ls.map((l) => ({ ...l, sub: "", kmValido: true, kmMsg: "", sbDeTrabalho: "" })));
  }, [coordenador]);

  /* auto nome/tel pelo ativo */
  const ativosKey = useMemo(() => JSON.stringify(linhas.map((l) => l.ativo)), [linhas]);
  useEffect(() => {
    setLinhas((ant) =>
      ant.map((linha) => {
        if (!linha.ativo || !ativos.length) return linha;

        const alvo = String(linha.ativo || "").toLowerCase();
        let ativoObj = null;

        for (const a of ativos) {
          if (typeof a === "object") {
            const nome = (a.ativos || a.ativo || a.nome || "").toLowerCase();
            if (nome && nome === alvo) {
              ativoObj = a;
              break;
            }
          } else if (typeof a === "string") {
            if (a.toLowerCase() === alvo) {
              ativoObj = { ativos: a };
              break;
            }
          }
        }

        if (!ativoObj) return linha;

        const tel = ativoObj.telefone_do_responsavel || "";
        const resp = ativoObj.responsavel || ativoObj.nome_responsavel || ativoObj.responsavel_nome || "";

        let novo = linha;
        if (tel && tel !== linha.telefone_do_reponsavel) novo = { ...novo, telefone_do_reponsavel: tel };
        if (resp && resp !== linha.nome_do_responsavel) novo = { ...novo, nome_do_responsavel: resp };
        return novo;
      })
    );
  }, [ativosKey, ativos]);

  /* SB datalist */
  const allSBs = useMemo(() => {
    const list = (sboePairs || [])
      .map((p) => p.sb)
      .filter((v) => v != null && String(v).trim() !== "");
    return Array.from(new Set(list));
  }, [sboePairs]);

  /* KM + SB */
  const prevKMRef = useRef([]);
  const validarKmEAtualizarSB = useCallback(async (linhaId, kmInicio, kmFinal, sub) => {
    if (!sub) {
      setLinhas((ls) =>
        ls.map((l) => (l.id === linhaId ? { ...l, kmValido: true, kmMsg: "", sbDeTrabalho: "" } : l))
      );
      return;
    }
    try {
      const v = await wf1Api.validarKm(sub, kmInicio, kmFinal);
      const kmValido = !!v?.data?.existe;

      let sbDeTrabalho = "";
      let kmMsg = "";

      if (kmValido) {
        const r = await wf1Api.sbPorKm(sub, kmInicio, kmFinal);
        const sbs = r?.data?.ok && Array.isArray(r?.data?.sbs) ? r.data.sbs : [];
        sbDeTrabalho = sbs.length ? Array.from(new Set(sbs)).join(", ") : "";
        if (!sbDeTrabalho) kmMsg = "Não há SB para o intervalo.";
      } else {
        kmMsg = "KM não existe na SUB.";
      }

      setLinhas((ls) =>
        ls.map((l) =>
          l.id === linhaId ? { ...l, kmValido, kmMsg, sbDeTrabalho: kmValido ? sbDeTrabalho : "" } : l
        )
      );
    } catch {
      setLinhas((ls) =>
        ls.map((l) =>
          l.id === linhaId ? { ...l, kmValido: false, kmMsg: "Erro ao validar KM/SB.", sbDeTrabalho: "" } : l
        )
      );
    }
  }, []);

  const kmKeys = useMemo(() => linhas.map((l) => [l.sub, l.kmInicio, l.kmFinal].join("-")).join(","), [linhas]);
  useEffect(() => {
    linhas.forEach((linha, idx) => {
      const key = [linha.sub, linha.kmInicio, linha.kmFinal].join("-");
      if (prevKMRef.current[idx] !== key) {
        prevKMRef.current[idx] = key;

        const iniOk = !!linha.kmInicio && !isNaN(Number(linha.kmInicio)) && Number(linha.kmInicio) > 0;
        const fimOk = !!linha.kmFinal && !isNaN(Number(linha.kmFinal)) && Number(linha.kmFinal) > 0;

        if (linha.sub && iniOk && fimOk) {
          validarKmEAtualizarSB(linha.id, Number(linha.kmInicio), Number(linha.kmFinal), linha.sub);
        } else {
          setLinhas((ls) => ls.map((l) => (l.id === linha.id ? { ...l, kmValido: true, kmMsg: "", sbDeTrabalho: "" } : l)));
        }
      }
    });
  }, [kmKeys, linhas, validarKmEAtualizarSB]);

  /* ações */
  const adicionarLinha = () => {
    setLinhas((ant) => [...ant, linhaVazia(nextId)]);
    setNextId((v) => v + 1);
  };

  const removerLinha = (id) => {
    if (linhas.length === 1) return;
    setLinhas((ant) => ant.filter((l) => l.id !== id));
  };

  const duplicarLinha = (id) => {
    setLinhas((prev) => {
      const idx = prev.findIndex((l) => l.id === id);
      if (idx === -1) return prev;
      const novo = { ...prev[idx], id: nextId, codigo_unico: "", request_id: "" };
      const arr = [...prev];
      arr.splice(idx + 1, 0, novo);
      return arr;
    });
    setNextId((v) => v + 1);
  };

  const podePreencher = !!gerencia && !!coordenador;

  const validarLinha = (linha) => {
    const atividadeOk = atividades.some(
      (a) => (a.atividade || "").toLowerCase() === (linha.atividade || "").toLowerCase()
    );

    const ativoOk = ativos.some((a) =>
      typeof a === "object"
        ? String(a.ativos || a.ativo || a.nome || "").toLowerCase() === String(linha.ativo || "").toLowerCase()
        : String(a || "").toLowerCase() === String(linha.ativo || "").toLowerCase()
    );

    const subOk = subs.some((s) => String(s.subdivisao_id) === String(linha.sub));

    const socadoraOk =
      (linha.atividade || "").trim().toLowerCase() !== "socadora" || (linha.os && linha.os.trim() !== "");

    const exige = ativoExigePatios(linha.ativo);
    const patioOrigemOk = !exige || !!String(linha.sbOrigem || "").trim();
    const patioEncerramentoOk = !exige || !!String(linha.sbEncerramento || "").trim();

    const kmOk =
      !!linha.kmValido &&
      !!linha.kmInicio &&
      !!linha.kmFinal &&
      Number(linha.kmInicio) > 0 &&
      Number(linha.kmFinal) > 0;

    const sbOk = !!String(linha.sbDeTrabalho || "").trim();

    const tempoOk =
      !linha.tempo_do_intervalo ||
      /^([01]\d|2[0-3]):([0-5]\d)(?::([0-5]\d))?$/.test(linha.tempo_do_intervalo);

    const qtdNum = parseQtdIntBR(linha.quantidade);
    const qtdOk = Number.isFinite(qtdNum) && qtdNum >= 0; // ajuste se quiser exigir > 0

    return (
      atividadeOk &&
      ativoOk &&
      subOk &&
      socadoraOk &&
      patioOrigemOk &&
      patioEncerramentoOk &&
      kmOk &&
      sbOk &&
      tempoOk &&
      qtdOk &&
      !!gerencia &&
      !!coordenador
    );
  };

  const handleSalvarTodas = async () => {
    setSalvando(true);
    setErroSalvar("");

    try {
      for (const linha of linhas) {
        if (!validarLinha(linha)) {
          setErroSalvar("Confira os campos obrigatórios/validações em todas as linhas.");
          setSalvando(false);
          return;
        }

        const idem = linha.request_id || genIdempotencyKey();

        const qtdNum = parseQtdIntBR(linha.quantidade);
        if (!Number.isFinite(qtdNum)) {
          setErroSalvar("Quantidade inválida.");
          setSalvando(false);
          return;
        }

        const payload = {
          data: linha.data,
          gerencia,
          coordenador,
          atividade: linha.atividade,
          ativo: linha.ativo,

          equipamento: linha.equipamento || "",
          sub: linha.sub,

          sbOrigem: linha.sbOrigem || "",
          sbDeTrabalho: linha.sbDeTrabalho || "",

          kmInicio: linha.kmInicio,
          kmFinal: linha.kmFinal,

          sbEncerramento: linha.sbEncerramento || "",

          tempo_do_intervalo: linha.tempo_do_intervalo,
          quantidade: qtdNum, // <<< NUMÉRICO REAL (1000), não "1.000"

          os: linha.os || "",
          unidade_de_medida: linha.unidade_de_medida || "",
          observacao: linha.observacao || "",

          nome_do_responsavel: linha.nome_do_responsavel || "",
          telefone_do_reponsavel: linha.telefone_do_reponsavel || "",
        };

        const r = await wf1Api.createW1(payload, idem);
        const codigo = r?.data?.codigo_unico || "";

        setLinhas((ls) => ls.map((l) => (l.id === linha.id ? { ...l, codigo_unico: codigo, request_id: idem } : l)));
      }

      setLinhas([linhaVazia(1)]);
      setNextId(2);
    } catch (e) {
      setErroSalvar(
        `Erro ao salvar (HTTP ${e?.response?.status || "500"}): ${e?.response?.data?.message || e.message}`
      );
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className={styles.layout}>
      <W1Sidebar
        gerencias={gerencias}
        gerencia={gerencia}
        setGerencia={setGerencia}
        coordenadores={coordenadores}
        coordenador={coordenador}
        setCoordenador={setCoordenador}
        onBack={onBack}
        onAddLine={adicionarLinha}
        onSaveAll={handleSalvarTodas}
        salvando={salvando}
        podePreencher={podePreencher}
        erroSalvar={erroSalvar}
      />

      <main className={styles.content}>
        <div className={styles.title}>
          WF_1 — PROGRAMAÇÃO INICIAL
          <br />
          <span>
            {gerencia ? `GERÊNCIA: ${gerencia}` : ""}
            {gerencia && coordenador ? " | " : ""}
            {coordenador ? `COORDENADOR: ${coordenador}` : ""}
          </span>
        </div>

        {podePreencher && (
          <W1Table
            linhas={linhas}
            setLinhas={setLinhas}
            atividades={atividades}
            ativos={ativos}
            subs={subs}
            allSBs={allSBs}
            onDuplicar={duplicarLinha}
            onRemover={removerLinha}
          />
        )}
      </main>
    </div>
  );
}
